/**
 * NotificationProvider — the single source of truth for the notification
 * inbox, unread count, and live SSE stream.
 *
 * Wrap once near the top of the app (above any component that needs it).
 * Children get `useNotifications()` hook returning:
 *
 *   {
 *     items,              // [{id, category, title, body, url, priority, read, ...}]
 *     unread,             // int
 *     loading, error,
 *     categories,         // {key → {label, icon, accent, ...}}
 *     hasMore, loadMore,
 *     refresh,
 *     markRead(id),
 *     markAllRead(),
 *     remove(id),
 *     connected,          // SSE connection state
 *   }
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import useSSE from './useSSE';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Ctx = createContext(null);

export function useNotifications() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useNotifications must be used inside <NotificationProvider>');
  return c;
}

export default function NotificationProvider({ children }) {
  const { user } = useAuth();
  const isAuthed = !!user?.email;
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState({});
  const [connected, setConnected] = useState(false);
  const arrivalRef = useRef(0);

  // Load category metadata once
  useEffect(() => {
    if (!isAuthed) return;
    axios.get(`${API}/api/notifications/categories`, { withCredentials: true })
      .then(r => setCategories(r.data || {}))
      .catch(() => {});
  }, [isAuthed]);

  const refresh = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${API}/api/notifications/feed?limit=50`,
        { withCredentials: true },
      );
      setItems(data.items || []);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.next_cursor);
      setUnread(typeof data.unread === 'number' ? data.unread : 0);
    } catch (e) {
      setError(e?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  const loadMore = useCallback(async () => {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/notifications/feed?limit=50&cursor=${encodeURIComponent(cursor)}`,
        { withCredentials: true },
      );
      setItems(prev => [...prev, ...(data.items || [])]);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.next_cursor);
    } catch (_) { /* swallow */ }
    finally { setLoading(false); }
  }, [cursor, loading]);

  // Initial load when user becomes available
  useEffect(() => { if (isAuthed) refresh(); }, [isAuthed, refresh]);

  // SSE live stream.  Defer the connection by 1 second after auth so the
  // initial post-login render isn't competing with EventSource setup for
  // resources — iOS Safari is particularly sensitive to this and can crash
  // the tab if a heavy render coincides with the SSE connection start.
  const [sseEnabled, setSseEnabled] = useState(false);
  useEffect(() => {
    if (!isAuthed) { setSseEnabled(false); return; }
    const t = setTimeout(() => setSseEnabled(true), 1000);
    return () => clearTimeout(t);
  }, [isAuthed]);

  useSSE(sseEnabled ? `${API}/api/notifications/stream` : null, {
    onOpen: () => setConnected(true),
    onError: () => setConnected(false),
    notification: (payload) => {
      // Insert at top, dedupe by id, bump unread
      setItems((prev) => {
        if (prev.find(x => x.id === payload.id)) return prev;
        return [payload, ...prev].slice(0, 200);
      });
      setUnread((u) => u + 1);
      arrivalRef.current = Date.now();
      // Emit a window event so isolated components (the bell pulse, toast)
      // can react without subscribing to the context.
      try {
        window.dispatchEvent(new CustomEvent('cs-notification', { detail: payload }));
      } catch (_) { /* noop */ }
    },
  });

  // Optimistic markRead
  const markRead = useCallback(async (id) => {
    setItems(prev => prev.map(x => x.id === id ? { ...x, read: true } : x));
    setUnread(u => Math.max(0, u - 1));
    try {
      await axios.post(`${API}/api/notifications/feed/${id}/read`, {}, { withCredentials: true });
    } catch (_) { /* server will be re-synced on next refresh */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    setUnread(0);
    try {
      await axios.post(`${API}/api/notifications/feed/read-all`, {}, { withCredentials: true });
    } catch (_) { /* noop */ }
  }, []);

  const remove = useCallback(async (id) => {
    setItems(prev => {
      const removed = prev.find(x => x.id === id);
      if (removed && !removed.read) setUnread(u => Math.max(0, u - 1));
      return prev.filter(x => x.id !== id);
    });
    try {
      await axios.delete(`${API}/api/notifications/feed/${id}`, { withCredentials: true });
    } catch (_) { /* noop */ }
  }, []);

  const value = {
    items, unread, loading, error, categories, hasMore,
    loadMore, refresh, markRead, markAllRead, remove,
    connected,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
